<template>
  <section class="bg-gradient-to-b from-white to-bg py-14 tablet:py-20">
    <div class="container">
      <div class="mx-auto max-w-3xl text-center">
        <p
          class="mb-3 font-display text-[28px] font-medium italic leading-tight tracking-tight text-gold-primary-120 tablet:mb-4 tablet:text-[40px]"
        >
          {{ t('landing.referralProgram.eyebrow') }}
        </p>
        <i18n-t
          keypath="landing.referralProgram.title"
          scope="global"
          tag="h2"
          class="text-2xl font-extrabold leading-[1.3] tracking-tight tablet:text-3xl"
        >
          <template #br><br /></template>
          <template #accent
            ><span class="text-gold-primary-100">{{ t('landing.referralProgram.titleAccent') }}</span></template
          >
        </i18n-t>
        <p class="mx-auto mt-4 max-w-2xl text-base text-black-80">
          {{ t('landing.referralProgram.description') }}
        </p>
      </div>

      <div
        class="mt-8 flex flex-col items-center gap-5 tablet:mt-10 tablet:flex-row tablet:justify-between tablet:gap-3"
      >
        <!-- Node 1: You -->
        <div class="flex flex-col items-center gap-2">
          <div
            class="flex size-16 items-center justify-center rounded-full bg-gold-primary-120 text-white shadow-lg shadow-gold-primary-100/30 tablet:size-20"
          >
            <svg
              viewBox="0 0 24 24"
              class="size-7"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
              <circle
                cx="12"
                cy="7"
                r="4"
              />
            </svg>
          </div>
          <p class="max-w-[120px] text-center text-xs font-bold text-black-100">
            {{ t('landing.referralProgram.you') }}
          </p>
        </div>

        <!-- Arrow 1 -->
        <div class="flex items-center justify-center text-black-60">
          <svg
            viewBox="0 0 100 10"
            preserveAspectRatio="none"
            class="hidden h-2.5 w-24 tablet:block rtl:-scale-x-100"
            stroke="currentColor"
            stroke-width="2"
            fill="none"
          >
            <line
              x1="0"
              y1="5"
              x2="90"
              y2="5"
              stroke-dasharray="4 4"
            />
            <polyline
              points="85,1 95,5 85,9"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <svg
            viewBox="0 0 24 24"
            class="size-5 rotate-90 tablet:hidden"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </div>

        <!-- Node 2: Friend joins Insiders -->
        <div class="flex flex-col items-center gap-2">
          <div
            class="flex size-16 items-center justify-center rounded-full bg-white text-gold-primary-120 ring-2 ring-gold-primary-100 tablet:size-20"
          >
            <svg
              viewBox="0 0 24 24"
              class="size-7"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
              <circle
                cx="9"
                cy="7"
                r="4"
              />
              <path d="M23 21v-2a4 4 0 00-3-3.87" />
              <path d="M16 3.13a4 4 0 010 7.75" />
            </svg>
          </div>
          <p class="max-w-[120px] text-center text-xs font-bold text-black-100">
            {{ t('landing.referralProgram.friendJoins') }}
          </p>
        </div>

        <!-- Arrow 2 -->
        <div class="flex items-center justify-center text-black-60">
          <svg
            viewBox="0 0 100 10"
            preserveAspectRatio="none"
            class="hidden h-2.5 w-24 tablet:block rtl:-scale-x-100"
            stroke="currentColor"
            stroke-width="2"
            fill="none"
          >
            <line
              x1="0"
              y1="5"
              x2="90"
              y2="5"
              stroke-dasharray="4 4"
            />
            <polyline
              points="85,1 95,5 85,9"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <svg
            viewBox="0 0 24 24"
            class="size-5 rotate-90 tablet:hidden"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </div>

        <!-- Node 3: Friend makes sales -->
        <div class="flex flex-col items-center gap-2">
          <div
            class="flex size-16 items-center justify-center rounded-full bg-white text-gold-primary-120 ring-2 ring-gold-primary-100 tablet:size-20"
          >
            <svg
              viewBox="0 0 24 24"
              class="size-7"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M3 21h18" />
              <path d="M5 21V7l8-4v18" />
              <path d="M19 21V11l-6-4" />
            </svg>
          </div>
          <p class="max-w-[120px] text-center text-xs font-bold text-black-100">
            {{ t('landing.referralProgram.friendSells') }}
          </p>
        </div>

        <!-- Arrow 3 (emphasized) -->
        <div class="flex items-center justify-center text-gold-primary-120">
          <svg
            viewBox="0 0 100 10"
            preserveAspectRatio="none"
            class="hidden h-2.5 w-24 tablet:block rtl:-scale-x-100"
            stroke="currentColor"
            stroke-width="3"
            fill="none"
          >
            <line
              x1="0"
              y1="5"
              x2="90"
              y2="5"
            />
            <polyline
              points="85,1 95,5 85,9"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <svg
            viewBox="0 0 24 24"
            class="size-5 rotate-90 tablet:hidden"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </div>

        <!-- Node 4: Reward -->
        <div class="flex flex-col items-center gap-2">
          <div
            class="relative flex size-16 items-center justify-center rounded-full bg-gradient-to-b from-gold-primary-100 to-gold-primary-120 text-lg font-black text-black-100 shadow-lg shadow-gold-primary-100/30 tablet:size-20 tablet:text-xl"
          >
            20%
            <span
              class="absolute -end-2 -top-2 rounded-full bg-black-100 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white"
              >{{ t('landing.referralProgram.lifetime') }}</span
            >
          </div>
          <p class="max-w-[140px] text-center text-xs font-bold text-gold-primary-120">
            {{ t('landing.referralProgram.reward') }}
          </p>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
const { t } = useI18n();
</script>
